import * as yup from 'yup';
import type { CreativeEntry } from 'types/shared';
import type { PinterestCampaignWithAdGroups } from '../api/types';

export const PINTEREST_STEP = {
  CREATIVES: 0,
  ACCOUNT: 1,
  CAMPAIGNS: 2,
  AD_GROUPS: 3,
  AD: 4,
} as const;

export const PINTEREST_VALIDATION_MESSAGE = {
  creatives: 'At least one creative has to be selected',
  account: "Can't be empty",
  campaigns: 'At least one campaign has to be selected',
  adGroups: 'At least one ad group has to be selected',
  adGroupsPerCampaign:
    'At least one ad group must be selected for each selected campaign that has ad groups with a flow',
} as const;

export const pinterestSchema = yup
  .object()
  .shape({
    currentStep: yup.number().default(0).optional(),
    app: yup.string().required("Can't be empty"),
    accountId: yup.string().default(''),
    selectedCreatives: yup.array().of(yup.string().required()).default([]),
    selectedCreativesOnly: yup.bool().default(false),
    filterCreativeByName: yup.array().of(yup.string().required()).default([]),
    selectedCampaigns: yup.array().of(yup.string().required()).default([]),
    selectedCampaignsOnly: yup.bool().default(false),
    filterCampaignsByName: yup.array().of(yup.string().required()).default([]),
    selectedAdGroups: yup.array().of(yup.string().required()).default([]),
    filterAdGroupsByName: yup.array().of(yup.string().required()).default([]),
    $creativesRows: yup.mixed<CreativeEntry[]>().optional(),
    $adGroupsData: yup.mixed<PinterestCampaignWithAdGroups[]>().optional(),
  })
  .test('step-validation', 'Step validation failed', function (value) {
    const currentStep = value.currentStep ?? 0;

    if (currentStep === PINTEREST_STEP.CREATIVES && value.selectedCreatives.length === 0) {
      return this.createError({ path: 'selectedCreatives', message: PINTEREST_VALIDATION_MESSAGE.creatives });
    }

    if (currentStep === PINTEREST_STEP.ACCOUNT && !value.accountId) {
      return this.createError({ path: 'accountId', message: PINTEREST_VALIDATION_MESSAGE.account });
    }

    if (currentStep === PINTEREST_STEP.CAMPAIGNS && value.selectedCampaigns.length === 0) {
      return this.createError({ path: 'selectedCampaigns', message: PINTEREST_VALIDATION_MESSAGE.campaigns });
    }

    if (currentStep === PINTEREST_STEP.AD_GROUPS) {
      if (value.selectedAdGroups.length === 0) {
        return this.createError({ path: 'selectedAdGroups', message: PINTEREST_VALIDATION_MESSAGE.adGroups });
      }

      const adGroupsData = value.$adGroupsData;
      if (Array.isArray(adGroupsData)) {
        const selected = new Set(value.selectedAdGroups);
        // A campaign whose ad groups all lack a parsed flow has nothing selectable,
        // so it cannot be required to have a selection.
        const everyCampaignCovered = value.selectedCampaigns.every((campaignId) => {
          const campaign = adGroupsData.find((entry) => entry.campaign_id === campaignId);
          if (!campaign) return true;

          const selectable = campaign.ad_groups.filter((adGroup) => !!adGroup.flow);
          if (!selectable.length) return true;

          return selectable.some((adGroup) => selected.has(adGroup.ad_group_id));
        });

        if (!everyCampaignCovered) {
          return this.createError({
            path: 'selectedAdGroups',
            message: PINTEREST_VALIDATION_MESSAGE.adGroupsPerCampaign,
          });
        }
      }
    }

    return true;
  });

export type PinterestFormValues = yup.InferType<typeof pinterestSchema>;
